import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, Heart } from "lucide-react";
import { toast } from "sonner";

import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import site from "@/data/site";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PageHeader() {
  return (
    <section className="relative bg-bloom overflow-hidden">
      <div className="container-luxe relative py-20 lg:py-28 text-center">
        <Reveal>
          <span className="eyebrow">Contact</span>
          <h1 className="display-xl font-display mt-4">
            Reach Out
            <span className="block font-script italic text-rose-ink leading-[1.05] mt-1">With Care.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-ink max-w-2xl mx-auto leading-relaxed">
            For pricing, booking, and availability, please contact us directly via email or phone.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function encode(data) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k] ?? ""))
    .join("&");
}

function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    preferred_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setSubmitting(true);

    // Submit to Netlify Forms (works on Netlify deploy) and to the backend in parallel.
    // Either succeeding counts as a successful submission.
    const netlifyPromise = fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", ...form }),
    })
      .then((r) => r.ok)
      .catch(() => false);

    const backendPromise = axios
      .post(`${API}/contact`, form)
      .then(() => true)
      .catch(() => false);

    const [netlifyOk, backendOk] = await Promise.all([netlifyPromise, backendPromise]);

    if (netlifyOk || backendOk) {
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", service_type: "", preferred_date: "", message: "" });
      toast.success("Message sent. We will reach out shortly.");
    } else {
      toast.error("Unable to send right now. Please email us directly.");
    }
    setSubmitting(false);
  };

  return (
    <section className="section bg-ivory">
      <div className="container-luxe grid lg:grid-cols-12 gap-12">
        {/* Info column */}
        <Reveal className="lg:col-span-5">
          <span className="eyebrow">Contact Information</span>
          <h2 className="display-lg font-display mt-3">We're here, gently.</h2>
          <p className="mt-5 text-muted-ink leading-relaxed">
            Reach us by phone or email anytime. We'll respond with warmth and clarity.
          </p>

          <ul className="mt-10 space-y-6">
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-blush grid place-items-center shrink-0">
                <Phone className="w-4 h-4 text-rose-ink" strokeWidth={1.5} />
              </span>
              <div>
                <p className="eyebrow !text-charcoal/60">Phone</p>
                <a href={site.phoneHref} data-testid="contact-info-phone" className="font-display text-2xl link-soft">{site.phone}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-blush grid place-items-center shrink-0">
                <Mail className="w-4 h-4 text-rose-ink" strokeWidth={1.5} />
              </span>
              <div>
                <p className="eyebrow !text-charcoal/60">Email</p>
                <a href={site.emailHref} data-testid="contact-info-email" className="font-display text-2xl link-soft">{site.email}</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-blush grid place-items-center shrink-0">
                <MapPin className="w-4 h-4 text-rose-ink" strokeWidth={1.5} />
              </span>
              <div>
                <p className="eyebrow !text-charcoal/60">Service Area</p>
                <p className="font-display text-2xl">{site.cityLine}</p>
              </div>
            </li>
          </ul>

          <div className="mt-10 rounded-2xl overflow-hidden border border-line aspect-[4/3] shadow-[0_20px_50px_-25px_rgba(42,34,38,0.25)] img-zoom">
            <img
              src={site.images.flowers1}
              alt="Soft pink memorial bouquet"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="mt-5 rounded-2xl overflow-hidden border border-line aspect-[4/3] shadow-[0_20px_50px_-25px_rgba(42,34,38,0.25)]">
            <iframe
              title="Greater Houston service area map"
              src="https://www.google.com/maps?q=Houston%2C+TX&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>

        {/* Form column */}
        <Reveal className="lg:col-span-7">
          <div className="bg-white rounded-[1.75rem] border border-line p-7 md:p-10 shadow-[0_30px_60px_-30px_rgba(42,34,38,0.2)]">
            {submitted ? (
              <div className="text-center py-10" data-testid="contact-success">
                <span className="w-14 h-14 rounded-full bg-blush grid place-items-center mx-auto">
                  <Heart className="w-6 h-6 text-rose-ink" strokeWidth={1.5} />
                </span>
                <h3 className="display-md font-display mt-5">Thank you.</h3>
                <p className="text-muted-ink mt-3 max-w-md mx-auto">
                  Your message has reached us. We'll respond personally and gently, usually within one business day.
                </p>
                <button
                  className="btn-pill btn-ghost mt-8"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                name="contact"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                data-testid="contact-form"
                className="grid gap-5"
              >
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>Don't fill this out: <input name="bot-field" /></label>
                </p>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-charcoal">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      required
                      data-testid="contact-input-name"
                      className="mt-2 h-12 bg-ivory border-line focus-visible:ring-rose"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-charcoal">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      required
                      data-testid="contact-input-email"
                      className="mt-2 h-12 bg-ivory border-line focus-visible:ring-rose"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="phone" className="text-charcoal">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(832) 555-0123"
                      data-testid="contact-input-phone"
                      className="mt-2 h-12 bg-ivory border-line focus-visible:ring-rose"
                    />
                  </div>
                  <div>
                    <Label htmlFor="preferred_date" className="text-charcoal">Preferred Date</Label>
                    <Input
                      id="preferred_date"
                      name="preferred_date"
                      type="date"
                      value={form.preferred_date}
                      onChange={update("preferred_date")}
                      data-testid="contact-input-date"
                      className="mt-2 h-12 bg-ivory border-line focus-visible:ring-rose"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="service_type" className="text-charcoal">Type of Service</Label>
                  <Select
                    value={form.service_type}
                    onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}
                  >
                    <SelectTrigger
                      id="service_type"
                      data-testid="contact-select-service"
                      className="mt-2 h-12 bg-ivory border-line focus:ring-rose"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {site.serviceTypeOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Hidden mirror for native form submissions (Netlify) */}
                  <input type="hidden" name="service_type" value={form.service_type} />
                </div>

                <div>
                  <Label htmlFor="message" className="text-charcoal">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Share anything you'd like us to know, names, dates, wishes."
                    rows={5}
                    required
                    data-testid="contact-input-message"
                    className="mt-2 bg-ivory border-line focus-visible:ring-rose"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="btn-pill btn-primary justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : <>Send Message <Send className="w-4 h-4" /></>}
                </button>

                <p className="text-xs text-muted-ink text-center mt-1">
                  We use your information only to respond to your inquiry. By submitting you agree to be contacted by Eternal Care.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Eternal Care | Pink Hearse Houston, TX"
        description="Contact Eternal Care for pink hearse funeral transportation in Houston, Texas. Phone, email and a personalized contact form for pricing and availability."
        path="/contact"
      />
      <PageHeader />
      <ContactSection />
    </>
  );
}
